import React, { Fragment, useEffect, useRef, useState } from 'react';
import TextInput from '@sabre/spark-react-core/text-input';
import { chain, isEmpty } from 'lodash';
import { any, arrayOf, bool, func, objectOf, string } from 'prop-types';
import Select from 'react-dropdown-select';
import styled from 'styled-components';

const MultiSelect = ({
   placeHolder = '',
   key,
   className = '',
   completeSearchSet,
   rowIndex,
   selectedValues,
   options,
   multiSelectOnChangeHandler,
   showLabel = true,
   enableSelectAll = true,
}) => {
   const [isShowAll, setIsShowAll] = useState(true);
   const [searchString, setSearchString] = useState('');
   const isOnChangeUserAction = useRef(false);

   useEffect(() => {
      isOnChangeUserAction.current = false;
   }, [options]);

   const customContentRenderer = ({ state }) => {
      const displaySelected = chain(state.values)
         .sortBy('label')
         .map((o) => o.label)
         .join(',')
         .value();

      return (
         <TextInput
            className='multi-select-value-container'
            placeHolder={placeHolder}
            name={`${placeHolder.replace(/\s+/g, '').toLowerCase()}${rowIndex}`}
            label={showLabel ? placeHolder : ''}
            value={displaySelected}
            onFocus={(a) => (a.target.autocomplete = 'off')}
         />
      );
   };

   const handleOnClick = (methods, option) => {
      isOnChangeUserAction.current = true;
      methods.addItem(option);
   };

   const customDropdownRenderer = ({ props, methods }) => {
      const regexp = new RegExp(searchString, 'i');
      const isEnabled = isEmpty(searchString);

      return (
         <Fragment>
            <SearchAndToggle color={props.color}>
               {enableSelectAll && (
                  <Buttons className={`${isEnabled ? '' : 'spark-hidden'}`}>
                     {props.options.length !== 0 && methods.areAllSelected() ? (
                        <Button className='clear' onClick={methods.clearAll}>
                           Clear all
                        </Button>
                     ) : (
                        <Button onClick={methods.selectAll}>Select all</Button>
                     )}
                     {isShowAll ? (
                        <Button onClick={() => setIsShowAll(!isShowAll)}>
                           Show Selected
                        </Button>
                     ) : (
                        <Button
                           className='clear'
                           onClick={() => setIsShowAll(!isShowAll)}
                        >
                           Show All
                        </Button>
                     )}
                  </Buttons>
               )}
               <input
                  type='text'
                  value={searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                  placeholder='Search'
               />
            </SearchAndToggle>
            <Items>
               {props.options
                  .filter((item) =>
                     regexp.test(item[props.searchBy] || item[props.labelField])
                  )
                  .map((option) => {
                     const isSelected = methods.isSelected(option);
                     if (
                        (!props.keepSelectedInList && isSelected) ||
                        !(isShowAll || methods.isSelected(option))
                     ) {
                        return null;
                     }

                     return (
                        <Item
                           disabled={option.disabled}
                           key={option[props.valueField]}
                           onClick={
                              option.disabled
                                 ? null
                                 : () => handleOnClick(methods, option)
                           }
                        >
                           <input
                              type='checkbox'
                              onChange={() =>
                                 option.disabled
                                    ? undefined
                                    : handleOnClick(methods, option)
                              }
                              checked={isSelected}
                           />
                           <ItemLabel>{option[props.labelField]}</ItemLabel>
                        </Item>
                     );
                  })}
            </Items>
         </Fragment>
      );
   };

   return (
      <StyledSelect
         key={key}
         className={`fo-multi-select ${className}`}
         placeholder='Select'
         multi
         contentRenderer={customContentRenderer}
         dropdownRenderer={customDropdownRenderer}
         options={options}
         onChange={(selectedValues) =>
            multiSelectOnChangeHandler(
               selectedValues,
               completeSearchSet,
               rowIndex,
               options,
               isOnChangeUserAction.current
            )
         }
         values={isEmpty(selectedValues) ? [] : selectedValues}
         dropdownPosition='auto'
         backspaceDelete={false}
         clearable={selectedValues.length > 0}
         separator={true}
         clearOnBlur={true}
         onDropdownClose={() => setSearchString('')}
      />
   );
};

const StyledSelect = styled(Select)`
   .react-dropdown-select-dropdown {
      overflow: initial;
   }
`;

const SearchAndToggle = styled.div`
   display: flex;
   flex-direction: column;
   input {
      margin: 10px 10px 0;
      line-height: 30px;
      padding: 0px 20px;
      border: 1px solid #ccc;
      border-radius: 3px;
      :focus {
         outline: none;
         border: 1px solid deepskyblue;
      }
   }
`;

const Items = styled.div`
   overflow: auto;
   min-height: 10px;
   max-height: 200px;
`;

const Item = styled.div`
   display: flex;
   margin: 10px;
   align-items: center;
   ${({ disabled }) => disabled && 'text-decoration: line-through;'}
`;

const ItemLabel = styled.div`
   margin: 0px 8px;
   padding-top: 4px;
`;

const Buttons = styled.div`
   display: flex;
   justify-content: space-between;
   & div {
      margin: 10px 0 0 10px;
      font-weight: 600;
   }
`;

const Button = styled.button`
   background: none;
   border: 1px solid #555;
   color: #555;
   border-radius: 3px;
   margin: 10px 10px 0;
   padding: 3px 5px;
   font-size: 10px;
   text-transform: uppercase;
   cursor: pointer;
   outline: none;
   &.clear {
      background-color: tomato;
      border: 1px solid tomato;
      color: #fff;
   }
`;

MultiSelect.propTypes = {
   placeHolder: string,
   key: string,
   className: string,
   completeSearchSet: any,
   rowIndex: string,
   selectedValues: arrayOf(string),
   options: objectOf({
      labelField: string,
      valueField: string,
   }),
   multiSelectOnChangeHandler: func,
   showLabel: bool,
   enableSelectAll: bool,
};

export default MultiSelect;
